// Notification stack (right edge): Civ-style round icons in priority order. Clicking one
// expands a card to its left (title, date, body, sources, links, fly-to). Dismiss removes it;
// beyond what fits above Next Turn the rest collapse into a "+N" button.

import { useSignal, type Signal } from '@preact/signals';
import { useLayoutEffect, useRef } from 'preact/hooks';
import type { Notification, NotificationKind } from '../services/api.ts';
import { useServices } from './context.ts';
import { fmtDate } from './format.ts';
import { Icon, KIND_ICON } from './icons.tsx';
import { bodyOf, liftOf, noteClaim, splitLinks } from './notecard.ts';
import { ClaimLabel, SourceChip, useTip } from './parts.tsx';
import { lookOf } from './nation.ts';
import { hudSize, openNote, toggleNote } from './store.ts';

// Vertical budget (px) between the ribbon and the Next Turn button; see hud.css .wc-notes.
const NOTES_TOP = 164;
const NEXT_TURN_H = 215;
const MORE_H = 56;
const NOTE_SLOT = 58;
const VISIBLE_MIN = 2;
const VISIBLE_MAX = 8;

function visibleCount(hudHeight: number): number {
  const slots = Math.floor((hudHeight - NOTES_TOP - NEXT_TURN_H - MORE_H) / NOTE_SLOT);
  return Math.min(VISIBLE_MAX, Math.max(VISIBLE_MIN, slots));
}
const LINKS_SHOWN = 3;
/** Card top relative to its icon; see hud.css .wc-note-card. */
const CARD_TOP = -6;

/** Priority at or above which the icon carries an alert pip. */
const HIGH_PRIORITY = 8;

// Ring colour per kind: conflict red, politics blue, hazards orange, achievements gold.
const KIND_TONE: Record<NotificationKind, string> = {
  war: '#d2412f',
  control: '#d2412f',
  border: '#3fb4b0',
  membership: '#3f86d8',
  leader: '#3f86d8',
  milestone: '#e6c77a',
  wonder: '#c58cff',
  disaster: '#e58a2c',
  launch: '#e6c77a',
  event: '#9fb4c2',
};

export const KIND_LABEL: Record<NotificationKind, string> = {
  event: 'Current events',
  war: 'War',
  control: 'Territory',
  border: 'Border change',
  membership: 'Membership',
  leader: 'Leadership',
  milestone: 'Milestone',
  wonder: 'World Heritage',
  disaster: 'Live · hazard',
  launch: 'Live · launch',
};

/** Highest priority first, then newest. */
function byPriority(a: Notification, b: Notification): number {
  if (b.priority !== a.priority) {
    return b.priority - a.priority;
  }
  return b.date < a.date ? -1 : b.date > a.date ? 1 : 0;
}

export function NotificationStack() {
  const { state, actions } = useServices();
  const showAll = useSignal(false);
  const notes = [...state.notifications.value].sort(byPriority);
  if (notes.length === 0) {
    return null;
  }

  const max = visibleCount(hudSize.value.h);
  const visible = showAll.value ? notes : notes.slice(0, max);
  const hidden = notes.length - visible.length;

  return (
    <div class="wc-notes">
      {visible.map((n, i) => (
        <NoteIcon key={n.id} note={n} index={i} />
      ))}
      {hidden > 0 || showAll.value ? (
        <div class="wc-notes-more">
          <button
            type="button"
            class="wc-ibtn wc-notes-count"
            aria-label={showAll.value ? 'Collapse notifications' : `${hidden} more notifications`}
            onClick={() => {
              showAll.value = !showAll.value;
            }}
          >
            {showAll.value ? <Icon name="chevronUp" size={16} /> : <span class="wc-num">+{hidden}</span>}
          </button>
          <button
            type="button"
            class="wc-notes-clear"
            onClick={() => {
              for (const n of notes) {
                actions.dismiss(n.id);
              }
              openNote.value = null;
            }}
          >
            Clear all
          </button>
        </div>
      ) : null}
    </div>
  );
}

function NoteIcon({ note, index }: { note: Notification; index: number }) {
  const open = openNote.value === note.id;
  // While any card is open its neighbours' tips (which fly out to the left) would cover it.
  const carded = openNote.value !== null;
  const tone = KIND_TONE[note.kind];
  const handlers = useTip(
    () => (
      <>
        <div class="wc-tip-title">{note.title}</div>
        <div class="wc-year">
          {KIND_LABEL[note.kind]} · {fmtDate(note.date)}
        </div>
      </>
    ),
    'left',
  );

  return (
    <div class="wc-note" style={{ '--tone': tone, animationDelay: `${index * 40}ms` }}>
      {open ? <NoteCard note={note} /> : null}
      <button
        type="button"
        class={open ? 'wc-note-btn wc-note-btn--open' : 'wc-note-btn'}
        aria-expanded={open}
        aria-label={note.title}
        onClick={() => toggleNote(note.id)}
        {...(carded ? {} : handlers)}
      >
        <Icon name={KIND_ICON[note.kind]} size={24} />
        {note.priority >= HIGH_PRIORITY ? <span class="wc-note-pip" /> : null}
      </button>
    </div>
  );
}

function NoteCard({ note }: { note: Notification }) {
  const { actions } = useServices();
  const expanded = useSignal(false);
  const body = bodyOf(note.title, note.body);
  const claim = noteClaim(note.id, note.date);

  // A tall card opened from a low icon would run under the Next Turn button: raise it clear.
  // Measured in HUD pixels (offset*), which the HUD zoom does not distort.
  const card = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = card.current;
    const icon = el?.parentElement;
    const stack = icon?.parentElement;
    if (!el || !icon || !stack) {
      return;
    }

    const top = stack.offsetTop + icon.offsetTop + CARD_TOP;
    const lift = liftOf(top, top + el.offsetHeight, hudSize.value.h - NEXT_TURN_H, NOTES_TOP);
    el.style.setProperty('--lift', `${lift}px`);
  }, [note.id, body, claim?.text, expanded.value, hudSize.value.h]);

  return (
    <div ref={card} class="wc-note-card wc-panel" role="dialog" aria-label={note.title}>
      <div class="wc-note-kind">
        <Icon name={KIND_ICON[note.kind]} size={14} />
        {KIND_LABEL[note.kind]}
        <span class="wc-note-date">{fmtDate(note.date)}</span>
      </div>
      <div class="wc-note-title">{note.title}</div>
      {body !== null ? <p class="wc-note-body">{body}</p> : null}
      {claim ? (
        <div class="wc-note-claim">
          <ClaimLabel kind={claim.kind} text={claim.text} />
        </div>
      ) : null}
      {note.iso3s.length > 0 ? <NationTags iso3s={note.iso3s} /> : null}
      <div class="wc-note-src">
        <SourceChip ids={note.sources} />
        <NoteLinks links={note.links} expanded={expanded} />
      </div>
      <div class="wc-note-actions">
        {note.hex !== undefined ? (
          <button type="button" class="wc-btn" onClick={() => actions.flyTo(note.hex as number)}>
            <Icon name="target" size={14} />
            Fly to
          </button>
        ) : null}
        <button
          type="button"
          class="wc-btn"
          onClick={() => {
            actions.dismiss(note.id);
            openNote.value = null;
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

/** The references a card cites, as links to their hosts: the first few, the rest behind "+N more". */
export function NoteLinks({ links, expanded }: { links: string[]; expanded: Signal<boolean> }) {
  const { shown, more } = splitLinks(links, expanded.value ? links.length : LINKS_SHOWN);
  return (
    <>
      {shown.map((url) => (
        <a key={url} class="wc-note-link" href={url} target="_blank" rel="noreferrer">
          {hostOf(url)}
          <Icon name="external" size={11} />
        </a>
      ))}
      {more > 0 ? (
        <button type="button" class="wc-note-more" onClick={() => (expanded.value = true)}>
          +{more} more
        </button>
      ) : null}
    </>
  );
}

/** Nation names with an owner-colour swatch; the ISO3 stays in the title attribute. */
export function NationTags({ iso3s }: { iso3s: string[] }) {
  const { world } = useServices();
  return (
    <div class="wc-note-tags">
      {iso3s.map((c) => {
        const look = lookOf(world, c);
        return (
          <span key={c} class="wc-tag" title={c}>
            <span class="wc-swatch" style={{ background: look.colors.primary }} />
            {look.name}
          </span>
        );
      })}
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
